import { FC, useMemo  } from 'react';
import { PenaltyPaymentDto } from 'services/api/penalty-api.service';
import { Button, Table } from 'legacy-components/componets';
import { useNavigate } from 'react-router-dom';
import useGetMyEstateById from 'hooks/query/estates/use-get-my-estate-by-id';
import { TableColumn } from 'common/types/components/ui/table/table-props.type';
import { usePenaltyPayments } from 'hooks/query/penalty/use-penalty-payments';
import { Tooltip } from 'antd';
import { colors } from 'colors';

interface PenaltyTableRow {
  id: string;
  property: {
    address: string;
    estateId?: string | null;
  };
  type: string;
  amount: number;
  date: string;
  status: {
    isPaid: boolean;
    label: string;
  };
  actions: {
    penalty: PenaltyPaymentDto;
    onPay: (penaltyId: string) => void;
  };
}

const PropertyCell = ({ value }: { value: PenaltyTableRow['property'] }) => {
  const { data: estate, isLoading } = useGetMyEstateById(value.estateId || '');

  const formatAddress = () => {
    if (isLoading) return 'Loading...';
    if (!estate) return 'Address unavailable';
    const parts = [estate.street, estate.city, estate.state, estate.zipcode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Address unavailable';
  };

  return (
    <div className="text-sm">
      <div className="font-medium text-gray-900">{formatAddress()}</div>
    </div>
  );
};

const TypeCell = ({ value }: { value: PenaltyTableRow['type'] }) => (
  <div className="text-sm text-gray-600">
    {value}
  </div>
);

const AmountCell = ({ value }: { value: PenaltyTableRow['amount'] }) => (
  <div className="text-lg font-semibold text-gray-900">
    ${(value || 0).toFixed(2)}
  </div>
);

const DateCell = ({ value }: { value: PenaltyTableRow['date'] }) => (
  <div className="text-sm text-gray-600">
    {new Date(value).toLocaleDateString()}
  </div>
);

const StatusCell = ({ value }: { value: PenaltyTableRow['status'] }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-medium ${value.isPaid
    ? 'bg-green-100 text-green-800'
    : 'bg-orange-100 text-orange-800'
    }`}>
    {value.label}
  </span>
);

const ActionsCell = ({ value }: { value: PenaltyTableRow['actions'] }) => {
  if (value.penalty.isPaid) {
    return <span className="text-sm text-gray-400">Paid</span>;
  }

  return (
    <Button
      label="Pay Now"
      onClick={() => value.onPay(value.penalty.id)}
      className="text-white"
    />
  );
};

const LandlordPenalties: FC<{ penalties: PenaltyPaymentDto[] }> = ({ penalties }) => {
  const navigate = useNavigate();
  const { data: penaltiesData } = usePenaltyPayments();
  const bulkPenalty = penaltiesData?.bulkPenalty;

  const handlePayPenalty = (penaltyId: string) => {
    navigate(`/penalty-payment/${penaltyId}`);
  };

  // Ensure penalties is always an array
  const safePenalties = Array.isArray(penalties) ? penalties.filter(x => x.penaltyType == 'LateCountersign') : [];
  const unpaidPenalties = safePenalties.filter(p => p.status !== 'Paid');
  const totalUnpaidAmount = unpaidPenalties.reduce((sum, p) => sum + p.penaltyAmount, 0);
  
  // Check if bulk payment should be disabled
  const isBulkPaymentDisabled = bulkPenalty?.status === 'OnHold';


  const tableData: PenaltyTableRow[] = useMemo(() => {
    return safePenalties.map(penalty => ({
      id: penalty.id,
      property: {
        address: 'Property Address',
        estateId: penalty.estateId,
      },
      type: 'Late Countersign',
      amount: penalty.penaltyAmount,
      date: penalty.createdAt,
      status: {
        isPaid: penalty.status === 'Paid',
        label: penalty.status === 'PaymentInProcess' ? 'In Process' : penalty.status === 'OnHold' ? 'On Hold' : penalty.status,
      },
      actions: {
        penalty,
        onPay: handlePayPenalty,
      },
    }));
  }, [safePenalties]);

  const columns: TableColumn<PenaltyTableRow>[] = useMemo(() => [
    {
      Header: 'Property',
      accessor: 'property',
      Cell: PropertyCell,
      width: '30%',
      minWidth: 180,
    },
    {
      Header: 'Reason',
      accessor: 'type',
      Cell: TypeCell,
      width: '15%',
      minWidth: 90,
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: AmountCell,
      width: '15%',
      minWidth: 80,
    },
    {
      Header: 'Date',
      accessor: 'date',
      Cell: DateCell,
      width: '15%',
      minWidth: 80,
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: StatusCell,
      width: '10%',
      minWidth: 90,
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ActionsCell,
      width: '15%',
      minWidth: 90,
    },
  ], []);

  return (
    <div className='content grow px-2 md:px-0 md:pr-11 relative max-w-full overflow-x-auto'>
      <div className='flex flex-col gap-9 px-4 md:px-[34px] py-4 md:py-6 shadow-white-xl bg-white rounded-2xl'>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Service Fees</h2>
            <p className="text-sm text-gray-600 mt-1">
              {unpaidPenalties.length} unpaid penalties totaling ${totalUnpaidAmount.toFixed(2)}
            </p>
          </div>
        </div>
        <Table
          data={tableData}
          columns={columns}
          isLoading={false}
          placeholder={{
            title: 'No service fees found',
          }}
        />

        {unpaidPenalties.length > 1 && (
          <div className="flex justify-end">
            {isBulkPaymentDisabled ? (
              <Tooltip
                title="One of the penalties is in the process, please finish payment or wait"
                color={colors.white}
                mouseEnterDelay={0}
                arrow={false}
                overlayInnerStyle={{ color: colors.danger }}
              >
                <div>
                  <Button
                    label={`Pay Total $${totalUnpaidAmount.toFixed(2)} Service Fee`}
                    onClick={() => navigate('/penalty-payment/bulk')}
                    className="text-white"
                    disabled={isBulkPaymentDisabled}
                  />
                </div>
              </Tooltip>
            ) : (
              <Button
                label={`Pay Total $${totalUnpaidAmount.toFixed(2)} Service Fee`}
                onClick={() => navigate('/penalty-payment/bulk')}
                className="text-white"
                disabled={isBulkPaymentDisabled}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { LandlordPenalties };
